import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import type { textVariants } from './variants';

type TextVariant = VariantProps<typeof textVariants>['variant'];
type TextColor = VariantProps<typeof textVariants>['color'];

/**
 * variant に応じたデフォルトの color を決定
 *
 * @param variant - テキストのバリアント
 * @param color - 指定された色（オプション）
 * @returns 解決された色
 */
export const resolveColor = (variant: TextVariant, color: TextColor | undefined): TextColor => {
  if (color) return color;

  switch (variant) {
    case 'link':
      return 'link';
    case 'description':
      return 'secondary';
    default:
      return 'base';
  }
};

// Tailwind v4 のコンテンツスキャンに検出させるため、静的なクラス名で定義する。
// 動的な `line-clamp-${n}` ではビルド時にユーティリティが生成されない。
const LINE_CLAMP_CLASSES: Record<number, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

/**
 * 省略表示用の CSS クラスを生成
 *
 * @param truncate - 省略設定（true: 1行、数値: 指定行数。2〜6のみサポート）
 * @returns CSS クラス文字列
 */
export const getTruncateClasses = (truncate: boolean | number | undefined): string => {
  if (!truncate) return '';

  if (truncate === true || truncate === 1) {
    // 1行省略
    return 'truncate';
  }

  return LINE_CLAMP_CLASSES[truncate] ?? 'line-clamp-2';
};

/**
 * 空値のフォールバック表示を取得
 *
 * @param value - 表示する値
 * @returns 値があればそのまま、なければ '--'
 */
export const getDisplayValue = (value: ReactNode): ReactNode => {
  if (typeof value === 'number') return value;
  return value || '--';
};
